import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import type {ResourceDisplayReference} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {formatDateDMYHM, formatDateHM, isSameDay} from "crqlar-react-common/dist/utils/date.util";
import {type MouseEvent, useId, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {getReservationStatusColor} from "./colors";
import {getValidDateTimeValue, isReservationVisuallyInactive} from "./utils";

export interface ReservationElementItem {
    reservationId?: string;
    status?: ReservationStatus;
    start?: string | Date;
    end?: string | Date;
    serviceName?: string;
    resources?: ResourceDisplayReference[];
    restaurants?: ResourceDisplayReference[];
    tables?: ResourceDisplayReference[];
    guestCount?: number;
    eatPeriod?: string;
}

interface Props {
    reservations: ReservationElementItem[] | undefined;
    now: Date;
    onSelect?: (reservation: ReservationElementItem) => void;
    onAdd?: () => void;
    onCancelAll?: () => void;
    asPrint?: boolean;
    /** Uses flat rows for Raw print output and preserves item styling for Original. */
    printVariant?: "raw" | "original";
    showDate?: boolean;
    dateContext?: Date;
}

const UPCOMING_PREVIEW_LIMIT = 3;

const addButtonSx = {
    minWidth: 0,
    px: 1,
    py: 0,
    justifyContent: "flex-start",
    fontSize: 12,
    fontWeight: 500,
    textTransform: "none",
    "& .MuiButton-startIcon": {
        ml: 0,
        mr: 0.5,
    },
} as const;

const actionRowButtonSx = {
    height: 26,
    borderRadius: 1,
} as const;

const reservationStart = (reservation: ReservationElementItem) => (
    getValidDateTimeValue(reservation.start)?.getTime()
);

const hasStarted = (reservation: ReservationElementItem, now: Date) => {
    const start = reservationStart(reservation);
    return start !== undefined && start <= now.getTime();
};

const reservationTime = (
    reservation: ReservationElementItem,
    showDate: boolean,
    dateContext: Date | undefined,
) => {
    const start = getValidDateTimeValue(reservation.start);
    if (!start) return undefined;
    const includeDate = showDate || Boolean(dateContext && !isSameDay(start, dateContext));
    return includeDate ? formatDateDMYHM(start) : formatDateHM(start);
};

const resourceLabel = (resources: ResourceDisplayReference[] | undefined) => resources
    ?.map(resource => resource.name?.trim())
    .filter((name): name is string => Boolean(name))
    .join(", ");

const reservationLabel = (reservation: ReservationElementItem) => {
    const restaurantLabel = [
        resourceLabel(reservation.tables),
        resourceLabel(reservation.restaurants),
    ].filter(Boolean).join(" · ");

    return restaurantLabel
        || reservation.serviceName?.trim()
        || resourceLabel(reservation.resources);
};

const reservationAccentColor = (reservation: ReservationElementItem) => (
    reservation.restaurants?.map(resource => resource.tagColor).find(Boolean)
    ?? reservation.tables
        ?.map(resource => resource.parent?.tagColor ?? resource.tagColor)
        .find(Boolean)
);

const collapsedReservations = (
    reservations: ReservationElementItem[],
    now: Date,
) => {
    const firstUpcomingIndex = reservations.findIndex(reservation => !hasStarted(reservation, now));
    if (firstUpcomingIndex === -1) return reservations.slice(-1);

    const previousReservation = firstUpcomingIndex > 0
        ? [reservations[firstUpcomingIndex - 1]]
        : [];
    const upcomingReservations = reservations.slice(
        firstUpcomingIndex,
        firstUpcomingIndex + UPCOMING_PREVIEW_LIMIT,
    );

    return [...previousReservation, ...upcomingReservations];
};

export function ReservationsElement({
    reservations,
    now,
    onSelect,
    onAdd,
    onCancelAll,
    asPrint = false,
    printVariant = "raw",
    showDate = false,
    dateContext,
}: Props) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const menuId = useId();
    const orderedReservations = useMemo(() => (
        [...(reservations ?? [])].sort((left, right) => (
            (reservationStart(left) ?? Number.MAX_SAFE_INTEGER)
            - (reservationStart(right) ?? Number.MAX_SAFE_INTEGER)
        ))
    ), [reservations]);
    const isEmpty = orderedReservations.length === 0;
    const useFlatPrintStyle = asPrint && printVariant === "raw";

    if (isEmpty && !onAdd) return null;

    const collapsed = collapsedReservations(orderedReservations, now);
    const canToggle = !asPrint && collapsed.length < orderedReservations.length;
    const visibleReservations = asPrint
        ? orderedReservations
        : expanded ? orderedReservations : collapsed;
    const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onAdd?.();
    };

    return (
        <Box
            sx={{
                width: useFlatPrintStyle ? "100%" : undefined,
                maxWidth: useFlatPrintStyle ? "400px" : undefined,
                minWidth: 0,
                whiteSpace: "normal",
            }}
        >
            <Stack gap="4px">
                {visibleReservations.map((reservation, index) => {
                    const time = reservationTime(reservation, showDate, dateContext);
                    const accentColor = reservationAccentColor(reservation);
                    const eatPeriod = reservation.eatPeriod && reservation.eatPeriod !== "blank"
                        ? t(`Eat period ${reservation.eatPeriod}`)
                        : undefined;
                    const label = [eatPeriod, reservationLabel(reservation)].filter(Boolean).join(" · ");
                    const visuallyInactive = isReservationVisuallyInactive(
                        reservation.status,
                        reservation.start,
                        reservation.end,
                        now,
                    );
                    const interactive = !asPrint && Boolean(onSelect);

                    return (
                        <Box
                            key={reservation.reservationId ?? index}
                            component={interactive ? "button" : "div"}
                            type={interactive ? "button" : undefined}
                            onClick={interactive ? (event: MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                onSelect?.(reservation);
                            } : undefined}
                            sx={{
                                appearance: "none",
                                width: "100%",
                                height: "28px",
                                minWidth: 0,
                                px: "8px",
                                py: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: 0,
                                overflow: "hidden",
                                textAlign: "left",
                                font: "inherit",
                                fontSize: "13px",
                                color: "text.primary",
                                backgroundColor: "background.paper",
                                ...(useFlatPrintStyle ? {
                                    border: 0,
                                    borderRadius: 0,
                                    boxShadow: "none",
                                } : {
                                    border: theme => `1px solid ${theme.palette.divider}`,
                                    borderLeftWidth: accentColor ? 3 : 1,
                                    borderLeftColor: accentColor ?? "divider",
                                    borderRadius: "6px",
                                    boxShadow: "0 1px 1px rgba(15, 23, 42, 0.03)",
                                }),
                                cursor: interactive ? "pointer" : "default",
                                ...(interactive ? {
                                    "&:focus-visible": {
                                        outline: "2px solid",
                                        outlineColor: "primary.main",
                                        outlineOffset: 1,
                                    },
                                } : {}),
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap="5px"
                                sx={{
                                    flex: "1 1 auto",
                                    minWidth: 0,
                                    opacity: !asPrint && visuallyInactive ? 0.5 : 1,
                                }}
                            >
                                {reservation.status && (
                                    <Tooltip title={t(`Reservation status ${reservation.status}`)} arrow>
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                bgcolor: getReservationStatusColor(reservation.status),
                                                flex: "0 0 auto",
                                            }}
                                        />
                                    </Tooltip>
                                )}

                                {time && (
                                    <Box
                                        component="span"
                                        sx={{
                                            flex: "0 0 auto",
                                            fontWeight: asPrint ? 600 : 700,
                                            fontVariantNumeric: "tabular-nums",
                                            lineHeight: "16px",
                                            mt: "2px",
                                        }}
                                    >
                                        {time}
                                    </Box>
                                )}

                                {label && (
                                    <Box
                                        component="span"
                                        sx={{
                                            minWidth: 0,
                                            lineHeight: "16px",
                                            mt: "2px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {label}
                                    </Box>
                                )}

                                {reservation.guestCount !== undefined && (
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: "auto",
                                            flex: "0 0 auto",
                                            color: asPrint ? "inherit" : "text.secondary",
                                            fontVariantNumeric: "tabular-nums",
                                            lineHeight: "16px",
                                            whiteSpace: "nowrap",
                                            mt: "2px",
                                        }}
                                    >
                                        {t("Guest count short", {count: reservation.guestCount})}
                                    </Box>
                                )}
                            </Stack>

                            {interactive && (
                                <Box
                                    component="span"
                                    sx={{
                                        width: 21,
                                        height: 16,
                                        display: "flex",
                                        alignItems: "center",
                                        flex: "0 0 auto",
                                        overflow: "hidden",
                                        opacity: !asPrint && visuallyInactive ? 0.5 : 1,
                                    }}
                                >
                                    <ChevronRightIcon
                                        sx={{
                                            width: 16,
                                            ml: "5px",
                                            fontSize: 16,
                                            flex: "0 0 auto",
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    );
                })}

                {!asPrint && isEmpty && onAdd && (
                    <Button
                        fullWidth
                        variant="text"
                        color="inherit"
                        size="small"
                        startIcon={<AddIcon sx={{fontSize: "16px !important"}}/>}
                        onClick={handleAdd}
                        sx={{
                            ...addButtonSx,
                            height: 28,
                            color: "text.secondary",
                            borderRadius: "6px",
                            backgroundColor: "transparent",
                            outline: "1px dashed",
                            outlineColor: "divider",
                            outlineOffset: -1,
                            "&:hover": {
                                color: "text.primary",
                                backgroundColor: "action.hover",
                                outlineColor: "text.secondary",
                            },
                            "&.Mui-focusVisible": {
                                color: "text.primary",
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: -2,
                            },
                        }}
                    >
                        {t("Add")}
                    </Button>
                )}

                {!asPrint && !isEmpty && (canToggle || onAdd || onCancelAll) && (
                    <Box sx={{display: "flex", alignItems: "center", minHeight: 28, gap: 0.5}}>
                        {onAdd && (
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<AddIcon sx={{fontSize: "16px !important"}}/>}
                                onClick={handleAdd}
                                sx={{...addButtonSx, ...actionRowButtonSx}}
                            >
                                {t("Add")}
                            </Button>
                        )}

                        <Box sx={{flex: 1, minWidth: 0}}/>

                        {canToggle && (
                            <Button
                                size="small"
                                color="inherit"
                                onClick={event => {
                                    event.stopPropagation();
                                    setExpanded(current => !current);
                                }}
                                sx={{
                                    ...actionRowButtonSx,
                                    minWidth: 0,
                                    px: 0.5,
                                    py: 0,
                                    fontSize: 12,
                                    textTransform: "none",
                                }}
                            >
                                {expanded ? t("Show less") : t("Show more")}
                            </Button>
                        )}

                        {onCancelAll && (
                            <>
                                <IconButton
                                    size="small"
                                    aria-label={t("More options")}
                                    aria-controls={menuAnchor ? menuId : undefined}
                                    aria-haspopup="menu"
                                    aria-expanded={menuAnchor ? "true" : undefined}
                                    onClick={event => {
                                        event.stopPropagation();
                                        setMenuAnchor(event.currentTarget);
                                    }}
                                    sx={{
                                        ...actionRowButtonSx,
                                        width: 26,
                                        p: 0.5,
                                    }}
                                >
                                    <MoreHorizIcon sx={{fontSize: 18}}/>
                                </IconButton>
                                <Menu
                                    id={menuId}
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={() => setMenuAnchor(null)}
                                >
                                    <MenuItem
                                        onClick={event => {
                                            event.stopPropagation();
                                            setMenuAnchor(null);
                                            onCancelAll();
                                        }}
                                    >
                                        {t("Cancel all reservations")}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
